/**
 * Push notification helper — Web Push standard (VAPID).
 *
 * Flow:
 *   1. Fetch the VAPID public key from `/api/push/vapid-public-key`.
 *   2. Request Notification permission.
 *   3. Subscribe via ServiceWorkerRegistration.pushManager.subscribe().
 *   4. POST the subscription to `/api/push/subscribe`.
 *
 * All errors bubble up so the caller can render a toast.
 */
import { api } from "@/lib/api";

/** Convert a URL-safe base64 VAPID public key to a Uint8Array. */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function pushPermission() {
  if (!("Notification" in window)) return "unsupported";
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

async function getSWRegistration() {
  // Wait until the SW is active — it may still be installing on first load.
  const reg = await navigator.serviceWorker.ready;
  return reg;
}

/** Returns whether push notifications are configured server-side (VAPID key present). */
export async function pushAvailability() {
  try {
    const { data } = await api.get("/push/vapid-public-key");
    return { configured: !!data?.configured && !!data?.public_key, public_key: data?.public_key };
  } catch {
    return { configured: false, public_key: null };
  }
}

/** Idempotent — safe to call multiple times. */
export async function enablePush() {
  if (!isPushSupported()) throw new Error("Notifications non supportées par ce navigateur");

  // Preflight: if VAPID is not configured on the server, fail early with a friendly
  // message the caller can hide silently (see PushAutoPrompt / PushBanner).
  const avail = await pushAvailability();
  if (!avail.configured) {
    const err = new Error("Notifications non configurées côté serveur");
    err.code = "PUSH_NOT_CONFIGURED";
    throw err;
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Permission refusée");

  const reg = await getSWRegistration();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(avail.public_key),
    });
  }
  const json = sub.toJSON();
  await api.post("/push/subscribe", { subscription: json });
  return { subscribed: true, endpoint: json.endpoint };
}

export async function disablePush() {
  if (!isPushSupported()) return { subscribed: false };
  const reg = await getSWRegistration();
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    try { await api.post("/push/unsubscribe", { subscription: sub.toJSON() }); } catch {}
    await sub.unsubscribe();
  } else {
    try { await api.post("/push/unsubscribe", { subscription: { endpoint: "" } }); } catch {}
  }
  return { subscribed: false };
}

export async function getPushStatus() {
  if (!isPushSupported()) return { supported: false, subscribed: false, permission: "unsupported" };
  const reg = await getSWRegistration();
  const sub = await reg.pushManager.getSubscription();
  return {
    supported: true,
    subscribed: !!sub,
    permission: Notification.permission,
    endpoint: sub?.endpoint,
  };
}

export async function sendTestPush() {
  const { data } = await api.post("/push/test", {});
  return data;
}
